import React, { memo, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import DynamicUserForm from "../components/form/DynamicUserForm";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCurrentUserClinicMapping } from "../context/UserClinicMappingContext";
import { useProfilePrefetch } from "../context/ProfilePrefetchContext";
import { useRegionalBackend } from "../context/RegionalBackendContext";
import { useDelayedVisibility } from "../hooks/useDelayedVisibility";
import Spinner from "../components/Spinner";
import { trackEvent } from "../utils/analytics/track";
import { getSafeInternalNextPath } from "../utils/navigation/recordingModeNav";
import { mergeProfileWithClinicMapping } from "../utils/recordingMode/mergeProfileWithClinicMapping";
import { buildStudyAccessPath } from "../utils/navigation/onboardingPaths";

/**
 * FormPage
 * --------
 * Route wrapper that mounts <DynamicUserForm/> and optionally pre-fills it from:
 *  1) navigation state (highest priority)
 *  2) prefetched cached profile metadata
 *  3) backend-owned profile context from `/v1/users/me` (final fallback)
 */
function FormPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { state } = location; // { prefill } | undefined
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const mapping = useCurrentUserClinicMapping();
  const { getCachedProfile } = useProfilePrefetch();
  const regionalBackend = useRegionalBackend();
  const showLoadingMessage = useDelayedVisibility(
    Boolean(user?.uid) && !mapping.resolved,
  );
  const returnTo = getSafeInternalNextPath(
    state?.returnTo || searchParams.get("next"),
    "/home",
  );
  const hasVerifiedStudyAccessPrefill = Boolean(
    state?.prefill?.id ?? state?.prefill?.subjectId,
  );

  const cachedPrefill = useMemo(() => {
    if (!user?.uid) return undefined;
    const cached = getCachedProfile(user.uid);
    if (cached?.status !== "ready") return undefined;
    return mergeProfileWithClinicMapping(cached.data, mapping);
  }, [user?.uid, getCachedProfile, mapping]);

  const backendPrefill = useMemo(() => {
    return regionalBackend.profile
      ? mergeProfileWithClinicMapping(regionalBackend.profile, mapping)
      : undefined;
  }, [regionalBackend.profile, mapping]);

  const mergedPrefill = useMemo(() => {
    const base = state?.prefill ?? cachedPrefill ?? backendPrefill ?? {};
    return mergeProfileWithClinicMapping(base, mapping);
  }, [state?.prefill, cachedPrefill, backendPrefill, mapping]);

  useEffect(() => {
    trackEvent("profile_form_view", {
      hasPrefillFromNav: !!state?.prefill,
      hasPrefillFromCache: !!cachedPrefill,
      hasPrefillFromBackend: !!backendPrefill,
    });
  }, [state?.prefill, cachedPrefill, backendPrefill]);

  if (user?.uid && !mapping.resolved) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-3">
        <Spinner />
        {showLoadingMessage ? <p>{t("regionalBackend.loading.form")}</p> : null}
      </div>
    );
  }

  if (
    user?.uid &&
    mapping.resolved &&
    !mapping.subjectId &&
    !hasVerifiedStudyAccessPrefill
  ) {
    return (
      <Navigate
        to={buildStudyAccessPath({
          search: location.search,
          next: returnTo !== "/home" ? returnTo : null,
        })}
        replace
      />
    );
  }

  return (
    <div className="form-page w-full h-full max-w-lg">
      <DynamicUserForm
        initialData={mergedPrefill}
        returnTo={returnTo}
      />
    </div>
  );
}

export default memo(FormPage);
